import { Description, Title } from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import React, { useEffect, useRef, useState } from 'react'
import { ClientOnly } from 'remix-utils/client-only'
import ElevationChart from '#app/components/elevation-chart.tsx'
import LeafletMap from '#app/components/leaflet-map.client.tsx'
import TrailCard from '#app/components/trail-card.tsx'
import TrailHelp from '#app/components/trail-help.tsx'
import {
	Dialog,
	DialogContent,
	DialogTrigger,
} from '#app/components/ui/dialog.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { ScrollArea } from '#app/components/ui/scroll-area.tsx'
import { type TrailCaptureLayoutProps } from './trail-capture-layouts.tsx'

export default function TrailCaptureDesktopLayout({
	trail,
	trailCapture,
	captureData,
	currentFrameId,
	updatedAtDisplay,
	positions,
	mapMarkers,
	blip,
	onNavigate,
	onPositionChange,
	children,
}: TrailCaptureLayoutProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const [isFullscreen, setIsFullscreen] = useState(false)

	const handleFullscreen = () => {
		if (!containerRef.current) return

		if (document.fullscreenElement) {
			void document.exitFullscreen()
		} else {
			containerRef.current.requestFullscreen().catch((error) => {
				console.error('Fullscreen error:', error)

				alert('An error occurred while trying to enter fullscreen mode.')
			})
		}
	}

	useEffect(() => {
		const onFullscreenChange = () => {
			setIsFullscreen(!!document.fullscreenElement)
		}
		document.addEventListener('fullscreenchange', onFullscreenChange)
		return () =>
			document.removeEventListener('fullscreenchange', onFullscreenChange)
	}, [])

	return (
		<main className="flex h-full grow">
			<ScrollArea className="h-full w-80 shrink-0 pr-2">
				<TrailCard key={trail.id} trail={trail} trailCapture={trailCapture} />
				<div className="px-4 py-2 text-xs opacity-60">
					Updated {updatedAtDisplay}
				</div>
			</ScrollArea>

			<div className="grid h-full min-h-0 grow grid-rows-fill-auto overflow-hidden">
				<div
					ref={containerRef}
					className="relative min-h-0 w-full overflow-hidden"
				>
					<div className="absolute top-2 left-2 z-10">
						<HelpDialog />
					</div>

					<div className="absolute inset-0 min-h-0">{children}</div>

					<div className="absolute top-2 right-2 z-10">
						<button
							className="full-screen-button text-2xl font-bold"
							aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
							title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
							onClick={handleFullscreen}
							type="button"
						>
							<Icon
								size="xxl"
								className="align h-full"
								name={isFullscreen ? 'exit-full-screen' : 'enter-full-screen'}
							/>
						</button>
					</div>
				</div>

				<div className="h-36 pt-2">
					{captureData ? (
						<ElevationChart
							trailCapture={trailCapture}
							data={captureData}
							currentFrameId={currentFrameId}
							onPositionChange={onPositionChange}
							navigateTo={onNavigate}
						/>
					) : (
						<div className="h-full w-full animate-pulse rounded-md bg-gray-200" />
					)}
				</div>
			</div>
			<div className="h-full w-80 shrink-0 overflow-hidden pl-2">
				<ClientOnly
					fallback={<div id="skeleton" className="h-full w-full bg-accent" />}
				>
					{() =>
						captureData ? (
							<LeafletMap
								className="h-full w-full"
								center={captureData.center}
								bounds={captureData.bounds}
								positions={positions}
								markers={mapMarkers}
								blip={blip}
								navigateTo={onNavigate}
							/>
						) : (
							<div className="h-full w-full animate-pulse rounded-md bg-gray-200" />
						)
					}
				</ClientOnly>
			</div>
		</main>
	)
}

export function HelpDialog() {
	const [isOpen, setIsOpen] = useState(false)

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen} modal={true}>
			<DialogTrigger asChild>
				<button
					type="button"
					className="help-button text-2xl font-bold"
					aria-label="Open help dialog"
					title="Open help dialog"
					onClick={() => setIsOpen(true)}
				>
					<Icon size="xxl" className="align h-full" name="info-circled" />
				</button>
			</DialogTrigger>
			<DialogContent className="h-[90%] max-h-[800px] w-[90%] max-w-3xl">
				<VisuallyHidden>
					<Title>Help</Title>
					<Description>
						How to use the 360° image, elevation chart, and map.
					</Description>
				</VisuallyHidden>
				<ScrollArea className="-me-6 h-full overflow-y-auto pe-6">
					<TrailHelp />
				</ScrollArea>
			</DialogContent>
		</Dialog>
	)
}
